import {
  DomainMap,
  RegionalDomain,
  getBartPortalUrl,
} from "src/config/BartLambdaConfig";

export interface LabelledLink {
  label: string;
  link: string;
}

interface TransactionInfo {
  fuse: {
    billingWf?: LabelledLink;
    transactionWf?: LabelledLink;
    fusenWf: LabelledLink;
    fusePlan?: {
      bartLink: string;
      fenderLink: string;
    };
    entitlement?: {
      entitlement: LabelledLink;
      operation?: LabelledLink;
      execution?: LabelledLink;
    };
  };
  subs?: LabelledLink;
  acid: LabelledLink;
  race?: LabelledLink;
  flash: LabelledLink;
  hal?: LabelledLink;
  matcher?: Record<string, string>;
}

const FUSE_BILLING_SERVICE_ORCA_DOMAIN_MAP: DomainMap<string> = {
  "prod-FE":
    "https://us-west-2.studio.orca.amazon.dev/#/clients/fuse-billing/executions/",
  "prod-US":
    "https://us-east-1.studio.orca.amazon.dev/#/clients/fuse-billing/executions/",
  "prod-EU":
    "https://eu-south-2.studio.orca.amazon.dev/#/clients/fuse-billing/executions/",
  default:
    "https://beta.us-west-2.studio.orca.amazon.dev/#/clients/fuse-billing/executions/",
};

const SUBS_LINK_PREFIX_DOMAIN_MAP: DomainMap<string> = {
  "prod-FE": "https://subscriptions-fe.amazon.com/#/subscription/",
  "prod-US": "https://subscriptions-na.amazon.com/#/subscription/",
  "prod-EU": "https://subscriptions-eu.amazon.com/#/subscription/",
  default: "https://subscriptions-na.integ.amazon.com/#/subscription/",
};

const ACID_LINK_PREFIX_DOMAIN_MAP: DomainMap<string> = {
  "prod-FE": "https://axon-ops-fe.aka.amazon.com/acid/report?AGGREGATION_KEY=",
  "prod-US": "https://axon-ops-na.aka.amazon.com/acid/report?AGGREGATION_KEY=",
  "prod-EU": "https://axon-ops-eu.aka.amazon.com/acid/report?AGGREGATION_KEY=",
  default: "https://axon-ops.integ.amazon.com/acid/report?AGGREGATION_KEY=",
};

export const RACE_LINK_PREFIX_DOMAIN_MAP: DomainMap<Record<string, string>> = {
  "prod-FE": {
    true: "https://race-website-fe.aka.amazon.com/deferral_search/index?namespace=uat&deferral-id=",
    false:
      "https://race-website-fe.aka.amazon.com/deferral_search/index?namespace=prod&deferral-id=",
  },
  "prod-US": {
    true: "https://race-website-na.aka.amazon.com/deferral_search/index?namespace=uat&deferral-id=",
    false:
      "https://race-website-na.aka.amazon.com/deferral_search/index?namespace=prod&deferral-id=",
  },
  "prod-EU": {
    true: "https://race-website-eu-south-2.aka.amazon.com/deferral_search/index?namespace=uat&deferral-id=",
    false:
      "https://race-website-eu-south-2.aka.amazon.com/deferral_search/index?namespace=prod&deferral-id=",
  },
  default: {
    true: "https://race-website.integ.amazon.com:13743/deferral_search/index?namespace=test&deferral-id=",
    false:
      "https://race-website.integ.amazon.com:13743/deferral_search/index?namespace=test&deferral-id=",
  },
};

const FLASH_LINK_PREFIX_DOMAIN_MAP: DomainMap<Record<string, string>> = {
  "prod-FE": {
    true: "https://flashui-gamma-fe.aka.amazon.com/search/transaction?realm=JPAmazon&domain=uat&transactionId=",
    false:
      "https://flashui-fe.aka.amazon.com/search/transaction?realm=JPAmazon&domain=prod&transactionId=",
  },
  "prod-US": {
    true: "https://flashui-gamma-na.aka.amazon.com/search/transaction?realm=USAmazon&domain=uat&transactionId=",
    false:
      "https://flashui-na.aka.amazon.com/search/transaction?realm=USAmazon&domain=prod&transactionId=",
  },
  "prod-EU": {
    true: "https://flashui-gamma-eu.aka.amazon.com/search/transaction?realm=EUAmazon&domain=uat&transactionId=",
    false:
      "https://flashui-eu.aka.amazon.com/search/transaction?realm=EUAmazon&domain=prod&transactionId=",
  },
  default: {
    true: "https://flashui-beta-na.integ.amazon.com/search/transaction?realm=USAmazon&domain=test&transactionId=",
    false:
      "https://flashui-beta-na.integ.amazon.com/search/transaction?realm=USAmazon&domain=test&transactionId=",
  },
};

const MATCHER_LINK_PREFIX_DOMAIN_MAP: DomainMap<Record<string, string>> = {
  "prod-FE": {
    true: "https://flashui-fe.aka.amazon.com/search/matcher?realm=JPAmazon&domain=uat&matchKey=",
    false:
      "https://flashui-fe.aka.amazon.com/search/matcher?realm=JPAmazon&domain=prod&matchKey=",
  },
  "prod-US": {
    true: "https://flashui-na.aka.amazon.com/search/matcher?realm=USAmazon&domain=uat&matchKey=",
    false:
      "https://flashui-na.aka.amazon.com/search/matcher?realm=USAmazon&domain=prod&matchKey=",
  },
  "prod-EU": {
    true: "https://flashui-eu.aka.amazon.com/search/matcher?realm=EUAmazon&domain=uat&matchKey=",
    false:
      "https://flashui-eu.aka.amazon.com/search/matcher?realm=EUAmazon&domain=prod&matchKey=",
  },
  default: {
    true: "https://flashui-beta-na.integ.amazon.com/search/matcher?realm=USAmazon&domain=test&matchKey=",
    false:
      "https://flashui-beta-na.integ.amazon.com/search/matcher?realm=USAmazon&domain=test&matchKey=",
  },
};

// HAL (Source2USLv2) reconciliation links. Only prod domains are clickable;
// non-prod (default) resolves to empty and is rendered as "N/A" in the UI.
const HAL_RECON_LINK_PREFIX_DOMAIN_MAP: DomainMap<Record<string, string>> = {
  "prod-FE": {
    true: "https://fe.beta.financehub.amazon.dev/completeness/transaction-search?",
    false: "https://fe.financehub.amazon.dev/completeness/transaction-search?",
  },
  "prod-US": {
    true: "https://na.beta.financehub.amazon.dev/completeness/transaction-search?",
    false: "https://na.financehub.amazon.dev/completeness/transaction-search?",
  },
  "prod-EU": {
    true: "https://eu.beta.financehub.amazon.dev/completeness/transaction-search?",
    false: "https://eu.financehub.amazon.dev/completeness/transaction-search?",
  },
  default: {
    true: "",
    false: "",
  },
};

const MATCHER_LINK_ACCOUNT_NUMBER_FORMATTER = "&accountNumber=";

const FUSE_PLAN_FENDER_PREFIX_DOMAIN_MAP: DomainMap<string> = {
  "prod-FE": "https://fender.corp.amazon.com/domains/prod-FE/plans/",
  "prod-US": "https://fender.corp.amazon.com/domains/prod-NA/plans/",
  "prod-EU": "https://fender.corp.amazon.com/domains/prod-EU/plans/",
  default: "https://fender.corp.amazon.com/domains/devo-FE/plans/",
};

const ENTITLEMENT_FUSE_PORTAL_PREFIX_DOMAIN_MAP: DomainMap<string> = {
  "prod-FE":
    "https://console.harmony.a2z.com/fuse-portal/prod-fe/entitlements/",
  "prod-US":
    "https://console.harmony.a2z.com/fuse-portal/prod-na/entitlements/",
  "prod-EU":
    "https://console.harmony.a2z.com/fuse-portal/prod-eu/entitlements/",
  default: "https://console.harmony.a2z.com/fuse-portal/devo-na/entitlements/",
};

const ENTITLEMENT_OPERATION_FORMATTER = "?splitPanel=operation%3A";
const ENTITLEMENT_EXECUTION_FORMATTER = "?splitPanel=execution%3A";

const ENTITLEMENT_EXECUTION_ID_PREFIX = "amzn1.fuse.ent_execution.";

export const getLinkPrefix = (
  prefixDomainMap: DomainMap<string>,
  domain: RegionalDomain,
) => prefixDomainMap[domain] ?? prefixDomainMap.default;

export const getEnvironmentAwareLinkPrefix = (
  prefixDomainEnvironmentMap: DomainMap<Record<string, string>>,
  domain: RegionalDomain,
  isTest: boolean,
) =>
  (prefixDomainEnvironmentMap[domain] ?? prefixDomainEnvironmentMap.default)[
    isTest.toString()
  ];

export const getTransactionInfo = (
  externalId: string,
  transactionType: string,
  isTest: boolean,
  domain: RegionalDomain,
  optional?: {
    fusePlanId?: string;
    entitlement?: {
      entitlementInternalId: string;
      operationInternalId?: string;
    };
    matcher?: {
      matchKey: string;
      accounts: Set<string>;
    };
  },
): TransactionInfo => {
  const externalIdPrefix = externalId.substring(0, externalId.lastIndexOf("."));
  const eventType = externalId.substring(externalId.lastIndexOf(".") + 1);

  const result: TransactionInfo = {
    fuse: {
      fusenWf: {
        label: `FUSEN:${externalId}`,
        link: `${getLinkPrefix(FUSE_BILLING_SERVICE_ORCA_DOMAIN_MAP, domain)}FUSEN:${externalId}`,
      },
      fusePlan: optional?.fusePlanId
        ? {
            bartLink: getBartPortalUrl(
              domain,
              `/getplan/${optional?.fusePlanId}`,
            ),
            fenderLink:
              getLinkPrefix(FUSE_PLAN_FENDER_PREFIX_DOMAIN_MAP, domain) +
              optional?.fusePlanId,
          }
        : undefined,
    },
    acid: {
      label: "",
      link: "",
    },
    flash: {
      label: "",
      link: "",
    },
  };

  let acidAggregationKey = externalId;
  let raceDeferralId;

  // with fuse_external_id being searchable (https://tiny.amazon.com/18ewpgyo3/codeamazpackFlasblobb468conf)
  // this likely will work as a catch-all for future transactions
  let flashIdempotenceId = externalId;

  // https://tiny.amazon.com/18044vd3k/codeamazpackFuseblobmainmode
  switch (transactionType) {
    case "SUBSCRIPTIONS":
    case "SUBSCRIPTION":
    case "ENTITLEMENT":
      if (externalIdPrefix.startsWith(ENTITLEMENT_EXECUTION_ID_PREFIX)) {
        // entitlement-driven

        const transactionWfObjectId = `FuseTransaction:${externalId}`;
        result.fuse.transactionWf = {
          label: transactionWfObjectId,
          link: `${getLinkPrefix(FUSE_BILLING_SERVICE_ORCA_DOMAIN_MAP, domain)}${transactionWfObjectId}`,
        };

        acidAggregationKey = externalIdPrefix;
        raceDeferralId = externalIdPrefix;

        const flashSuffix = eventType === "Cancelled" ? "Cancel" : eventType;
        flashIdempotenceId = `Axon.Fuse.Entitlement.${externalIdPrefix}.${flashSuffix}`;
      } else {
        // subs-driven
        const subsId = externalIdPrefix.substring(
          0,
          externalIdPrefix.lastIndexOf("-"),
        );
        const periodNumber = externalIdPrefix.substring(
          externalIdPrefix.lastIndexOf("-") + 1,
        );

        const billingWfObjectId = `FuseBilling:${externalId}`;
        result.fuse.billingWf = {
          label: billingWfObjectId,
          link: `${getLinkPrefix(FUSE_BILLING_SERVICE_ORCA_DOMAIN_MAP, domain)}${billingWfObjectId}`,
        };

        result.subs = {
          label: subsId,
          link: getLinkPrefix(SUBS_LINK_PREFIX_DOMAIN_MAP, domain) + subsId,
        };

        acidAggregationKey = `${subsId}:${periodNumber}`;
        raceDeferralId = externalIdPrefix;

        const flashSuffix = eventType === "Cancelled" ? "Cancel" : eventType;
        flashIdempotenceId = `Axon.Fuse.Subscriptions.${subsId}:${periodNumber}.${flashSuffix}`;
      }
      break;
    case "BREAKAGE":
      flashIdempotenceId = `Axon.Fuse.Breakage.${externalId}`;
      break;
    case "TRANSACTION":
      flashIdempotenceId = `Axon.Fuse.Transaction.${eventType}.${externalId}`;
      break;
    case "CHARGE":
      flashIdempotenceId = `Axon.Fuse.Charge.${eventType}.${externalId}`;
      break;
    case "PURCHASE":
      acidAggregationKey = externalIdPrefix;
      flashIdempotenceId = `Axon.Fuse.DigitalPurchase.${externalId}`;
      break;
    // for default cases that are not modelled in, just use best-guess value (already populated above)
  }

  if (!result.fuse.billingWf && !result.fuse.transactionWf) {
    result.fuse.transactionWf = {
      label: `FuseTransaction:${externalId}`,
      link: `${getLinkPrefix(FUSE_BILLING_SERVICE_ORCA_DOMAIN_MAP, domain)}FuseTransaction:${externalId}`,
    };
  }

  result.acid = {
    label: acidAggregationKey,
    link:
      getLinkPrefix(ACID_LINK_PREFIX_DOMAIN_MAP, domain) +
      acidAggregationKey.replaceAll(":", "%3A"),
  };

  if (raceDeferralId) {
    result.race = {
      label: raceDeferralId,
      link:
        getEnvironmentAwareLinkPrefix(
          RACE_LINK_PREFIX_DOMAIN_MAP,
          domain,
          isTest,
        ) + raceDeferralId,
    };
  }

  result.flash = {
    label: externalId,
    link:
      getEnvironmentAwareLinkPrefix(
        FLASH_LINK_PREFIX_DOMAIN_MAP,
        domain,
        isTest,
      ) + flashIdempotenceId,
  };

  // HAL (Source2USLv2) reconciliation link is only available on prod domains.
  const halLinkPrefix = getEnvironmentAwareLinkPrefix(
    HAL_RECON_LINK_PREFIX_DOMAIN_MAP,
    domain,
    isTest,
  );
  if (halLinkPrefix) {
    result.hal = {
      label: externalId,
      link: halLinkPrefix + encodeURIComponent(externalId),
    };
  }

  if (optional?.matcher) {
    const matchKey = optional.matcher.matchKey;

    const matcher: Record<string, string> = {};

    optional.matcher.accounts.forEach((account) => {
      matcher[account] =
        getEnvironmentAwareLinkPrefix(
          MATCHER_LINK_PREFIX_DOMAIN_MAP,
          domain,
          isTest,
        ) +
        matchKey +
        MATCHER_LINK_ACCOUNT_NUMBER_FORMATTER +
        account;
    });
    result.matcher = matcher;
  }

  if (optional?.entitlement) {
    const { entitlementInternalId, operationInternalId } = optional.entitlement;

    const entitlement = {
      label: entitlementInternalId,
      link: `${getLinkPrefix(ENTITLEMENT_FUSE_PORTAL_PREFIX_DOMAIN_MAP, domain)}${entitlementInternalId}`,
    };

    let operation, execution;
    if (operationInternalId) {
      operation = {
        label: operationInternalId,
        link: `${entitlement.link}${ENTITLEMENT_OPERATION_FORMATTER}${entitlementInternalId}%3A${operationInternalId}`,
      };
    }

    // entitlement execution ID is the external ID prefix for Mazuma/Synchroncity transactions
    if (externalIdPrefix.startsWith(ENTITLEMENT_EXECUTION_ID_PREFIX)) {
      execution = {
        label: externalIdPrefix,
        link: `${entitlement.link}${ENTITLEMENT_EXECUTION_FORMATTER}${entitlementInternalId}%3A${externalIdPrefix.substring(ENTITLEMENT_EXECUTION_ID_PREFIX.length)}`,
      };
    }

    result.fuse.entitlement = {
      entitlement,
      operation,
      execution,
    };
  }

  return result;
};
